import { applyValidatorToMultipleFields, groupValidators } from '@framework/ui/Organisms/FinalForm/validators/helpers';
import { validateRequiredField } from '@framework/ui/Organisms/FinalForm/validators/validateRequiredField';
import { validateRecaptchaVerified } from '@framework/ui/Organisms/FinalForm/validators/validateRecaptchaVerified';
import { validateEmail } from '@framework/ui/Organisms/FinalForm/validators/user/validateEmail';
import { validatePhoneNumber } from '@framework/ui/Organisms/FinalForm/validators/user/validatePhoneNumber';
import { validateName } from '@framework/ui/Organisms/FinalForm/validators/user/validateName';
import { validateMaxLength } from '@framework/ui/Organisms/FinalForm/validators/validateMaxLength';

import validateCallDetailsFields from '@evercall/call-form/ui/helpers/validateCallDetailsFields';
import { mapOptionToValue } from '@framework/data-helpers/selectOptionMappers';
import { validateSpeakers } from '@evercall/call-form/ui/helpers/validateSpeakers';

import { CALL_FORM_STEPS } from '../constants';
import { ICallFormStep, TScheduleCallFormValues } from '../interfaces';
import { QUESTIONS_OACC, QUESTIONS_WEBINAR } from '@evercall/call-form/ui/constants';
import { IField } from '@framework/ui/Organisms/FinalForm/validators/interfaces/Field';
import CallEvent from '@evercall/domain/CallEvent';
import { CallEventType } from '@evercall/domain/vo/call/CallEventType';
import { validateDateTo } from '@framework/ui/Organisms/FinalForm/validators/validateDateTo';

const email: IField = { fieldName: 'corporateEmail', fieldCaption: 'Email' };
const firstName: IField = { fieldName: 'firstName', fieldCaption: 'First Name' };
const lastName: IField = { fieldName: 'lastName', fieldCaption: 'Last Name' };

export default (
  values: TScheduleCallFormValues,
  currentStep: ICallFormStep,
): object | null => {
  const isWebinar = CallEvent.isWebinar(mapOptionToValue(values.eventTypeId) as CallEventType);

  switch (currentStep) {
    case CALL_FORM_STEPS.YOUR_DETAILS:
      return groupValidators(
        validateEmail({ fieldName: email.fieldName, value: values.corporateEmail }),
        applyValidatorToMultipleFields(
          {
            values,
            validator: validateName,
            fields: [firstName, lastName],
          },
        ),
        applyValidatorToMultipleFields(
          {
            values,
            validator: validateRequiredField,
            fields: [
              email,
              firstName,
              lastName,
            ],
          },
        ),
        validatePhoneNumber({
          fieldName: 'phoneNumber',
          fieldCaption: 'Phone Number',
          value: values.phoneNumber,
          isRequired: true,
        }),
      );
    case CALL_FORM_STEPS.CALL_DETAILS:
      return groupValidators(
        validateCallDetailsFields(values),
        isWebinar && validateSpeakers(values.speakers),
      );
    case CALL_FORM_STEPS.CALL_FORMAT:
      const questionFields = isWebinar ? [
        QUESTIONS_WEBINAR.QA_SESSION,
        QUESTIONS_WEBINAR.SEPARATED_SESSION_LINES,
        QUESTIONS_WEBINAR.SPEAKER_PRESENTING_PROCESS,
      ] : [
        QUESTIONS_OACC.ATTENDEE_CHECK_IN_PROCESS,
        QUESTIONS_OACC.QA_SESSION,
        QUESTIONS_OACC.SEPARATED_CONFERENCE_LINES,
        QUESTIONS_OACC.SPEAKER_PRESENTING_PROCESS,
      ];
      return groupValidators(
        applyValidatorToMultipleFields(
          {
            values,
            validator: validateRequiredField,
            fields: questionFields.map((question) => ({ fieldName: question.name, isShortErrorText: true })),
          },
        ),
      );
    case CALL_FORM_STEPS.ADD_ONS:
      if (isWebinar) {
        const questionFields = [
          QUESTIONS_WEBINAR.MP4,
          QUESTIONS_WEBINAR.WEB_REPLAY,
          QUESTIONS_WEBINAR.CALL_TRANSCRIPTION,
        ];
        return groupValidators(
          validateRecaptchaVerified({ fieldName: 'recaptcha', value: values.recaptcha }),
          validateMaxLength(
            {
              value: values.additionalNotes,
              fieldName: 'additionalNotes',
              fieldCaption: 'Additional Notes',
              length: 500,
            },
          ),
          applyValidatorToMultipleFields(
            {
              values,
              validator: validateRequiredField,
              fields: questionFields.map((question) => ({ fieldName: question.name, isShortErrorText: true })),
            },
          ),
        );
      }

      return groupValidators(
        validateRecaptchaVerified({ fieldName: 'recaptcha', value: values.recaptcha }),
        validateMaxLength(
          {
            value: values.additionalNotes,
            fieldName: 'additionalNotes',
            fieldCaption: 'Additional Notes',
            length: 500,
          },
        ),
        ...(values.roadshowSharedPresentation === 'Yes' ? [
          validateDateTo({
            dateToTimestamp: values.roadshowSharedPresentationDeactivationTime,
            dateFromTimestamp: values.roadshowSharedPresentationActivationTime,
            fieldName: 'roadshowSharedPresentationDeactivationTime',
            fieldCaption: 'Deactivation Time',
            dateFromFieldCaption: 'Activation Time',
          }),
        ] : []),
        applyValidatorToMultipleFields(
          {
            validator: validateRequiredField,
            values,
            fields: [
              {
                fieldName: QUESTIONS_OACC.ROADSHOW_SHARED_PRESENTATION.name,
                isShortErrorText: true,
              },
              ...(values.roadshowSharedPresentation === 'Yes' ? [
                {
                  fieldName: 'roadshowSharedPresentationActivationTime',
                  isShortErrorText: true,
                },
                {
                  fieldName: 'roadshowSharedPresentationDeactivationTime',
                  isShortErrorText: true,
                },
              ] : []),
              {
                fieldName: QUESTIONS_OACC.SPEAKER_DASHBOARD.name,
                isShortErrorText: true,
              },
              {
                fieldName: QUESTIONS_OACC.CALL_RECORDING.name,
                isShortErrorText: true,
              },
              {
                fieldName: QUESTIONS_OACC.RECORDED_REPLAY.name,
                isShortErrorText: true,
              },
              {
                fieldName: QUESTIONS_OACC.CALL_TRANSCRIPTION.name,
                isShortErrorText: true,
              },
            ],
          },
        ),
      );
    default:
      return null;
  }
};
